import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HttpHelper from '../../utils/HttpHelper';
import {
  ASSESSMENT_TEMPLATES,
  AssessmentTemplate,
  TemplateQuestion,
  TemplateQuestionType,
} from '../../utils/assessmentTemplates';
import QuizIcon from '@mui/icons-material/Quiz';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import './AssessmentTemplateCards.css';

const AssessmentTemplateCards: React.FC = () => {
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState<boolean>(true);
  const [creatingId, setCreatingId] = useState<string | null>(null);

  /**
   * Creates the assessment, then creates each question sequentially,
   * and finally navigates HR to the edit page.
   */
  const handleUseTemplate = async (template: AssessmentTemplate) => {
    if (creatingId) return; // prevent double clicks
    setCreatingId(template.id);

    try {
      // 1. Create the assessment
      const assessmentPayload = {
        assessment: {
          title: template.title,
          description: template.description,
          isOptional: template.isOptional,
        },
      };

      const assessmentRes = await HttpHelper.post(
        'assessments/create',
        assessmentPayload,
      );

      if (!assessmentRes?.assessmentId) {
        console.error('Failed to create assessment from template');
        setCreatingId(null);
        return;
      }

      const assessmentId = assessmentRes.assessmentId;

      // 2. Create each question
      for (const question of template.questions) {
        const questionPayload: Record<string, unknown> = {
          question: {
            text: question.text,
            type: question.type,
            maxGrade: question.maxGrade,
          },
        };

        // Attach possible choices for choice-based questions
        if (
          (question.type === TemplateQuestionType.SingleChoice ||
            question.type === TemplateQuestionType.MultipleChoice) &&
          question.possibleChoices
        ) {
          questionPayload.possibleChoices = question.possibleChoices.map(
            (choice) => ({
              option: choice.option,
              isCorrect: choice.isCorrect ? 1 : 0,
            }),
          );
        }

        await HttpHelper.post(
          `assessments/${assessmentId}/question/create`,
          questionPayload,
        );
      }

      // 3. Navigate to the edit page so HR can customize
      navigate(`/manage-assessments/edit/${assessmentId}`, {
        state: {
          title: template.title,
          description: template.description,
        },
      });
    } catch (error) {
      console.error('Error creating assessment from template:', error);
    } finally {
      setCreatingId(null);
    }
  };

  return (
    <div className="template-section">
      {/* ── Collapsible Header ────────────────────────────── */}
      <div
        className="template-section-header"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <AutoAwesomeIcon sx={{ color: '#4A90D9', fontSize: '1.35rem' }} />
        <h6>Quick-Start Templates</h6>
        <ExpandMoreIcon
          className={`toggle-icon ${expanded ? 'open' : ''}`}
        />
      </div>

      {/* ── Card Grid ─────────────────────────────────────── */}
      {expanded && (
        <div className="template-cards-grid">
          {ASSESSMENT_TEMPLATES.map((template) => {
            const isCreating = creatingId === template.id;

            return (
              <div className="template-card" key={template.id}>
                {/* Accent bar */}
                <div
                  className="template-card-accent"
                  style={{ background: template.color }}
                />

                <div className="template-card-body">
                  {/* Header: icon + title + category */}
                  <div className="template-card-header">
                    <div
                      className="template-icon"
                      style={{
                        backgroundColor: `${template.color}15`,
                      }}
                    >
                      {template.icon}
                    </div>
                    <div className="template-meta">
                      <h3>{template.title}</h3>
                      <span
                        className="template-category"
                        style={{
                          color: template.color,
                          backgroundColor: `${template.color}15`,
                        }}
                      >
                        {template.category}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="template-description">
                    {template.description}
                  </p>

                  {/* Footer */}
                  <div className="template-card-footer">
                    <span className="template-question-count">
                      <QuizIcon />
                      {template.questionCount}{' '}
                      {template.questionCount === 1 ? 'question' : 'questions'}
                    </span>

                    <button
                      className="template-use-btn"
                      style={{ backgroundColor: template.color }}
                      disabled={isCreating}
                      onClick={() => handleUseTemplate(template)}
                    >
                      {isCreating ? (
                        <>
                          <span className="template-spinner" />
                          Creating…
                        </>
                      ) : (
                        <>
                          <RocketLaunchIcon />
                          Use Template
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssessmentTemplateCards;
